import React, { useContext, useEffect, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useNavigationType,
} from "react-router-dom";
import Verified from "../../resources/icons/shield-tick.svg";
import EntityConstants from "../../configs/EntityConstants";
import { GET, POST } from "../../service/CRUDService";
import Revoked from "../../resources/icons/white_shiel.svg";
import CfLoader from "../../common/CfLoader/CfLoader";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import toast from "react-hot-toast";
import AuthContext from "../../store/auth-context";
import useInterceptedNavigate from "../../hooks/useInterceptedNavigate";
import DownloadIcon from "../../resources/icons/download-white-icon.svg";
import { DocumentPreview } from "../../common/DocumentPreview/DocumentPreview";

const clearLegacyStorage = () => {
  localStorage.removeItem("org_legacy_code");
  localStorage.removeItem("recipient_legacy_name");
  localStorage.removeItem("recipient_legacy_phone_number");
  localStorage.removeItem("recipient_legacy_email");
  localStorage.removeItem("programme_legacy_code");
  localStorage.removeItem("comments_legacy");
  localStorage.removeItem("document_legacy");
};

const ViewLegacyCredential = () => {
  const { certificate_id } = useParams();
  const authContext = useContext(AuthContext);

  const navigate = useInterceptedNavigate();
  const defaultNavigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const state = location.state || {};
  const {
    transaction_verification_log_id,
    issuer,
    transacion_payment_status,
    verification_cost_overall,
    transaction_order_id,
    transaction_uploaded_document,
    document_type,
    mode_of_verification,
    transaction_certificate_id,
    transaction_verification_status,
    payment_id,
    programme_name,
    programme_code,
    recipient_name,
    recipient_email,
    issuer_logo,
    issuer_organisation,
    verification_cost,
    transaction_comments,
    payment_signature,
    organisation_code,
    name,
    transaction_id,
    pdf_url,
    lpi_orderId,
    lpi_paymentId,
    lpi_paymentSignature,
    lpi_verificationLogId,
  } = location.state || {};
  const org_legacy_code = localStorage.getItem("org_legacy_code");
  const recipient_legacy_name = localStorage.getItem("recipient_legacy_name");
  const recipient_legacy_phone_number = localStorage.getItem(
    "recipient_legacy_phone_number"
  );
  const recipient_legacy_email = localStorage.getItem("recipient_legacy_email");
  const programme_legacy_code = localStorage.getItem("programme_legacy_code");
  const comments_legacy = localStorage.getItem("comments_legacy");
  const document_legacy = localStorage.getItem("document_legacy");
  const [text, setText] = useState("");
  const [verificationStatus, setVerificationStatus] = useState("");
  const [verificationRecipientName, setVerificationRecipientName] =
    useState("");
  const [issuerName, setIssuerName] = useState("");
  const [verificationUploadedDocument, setVerificationUploadedDocument] =
    useState("");
  const [verificationLogId, setVerificationLogId] = useState("");
  const [comments, setComments] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");

  const maxChars = 200;

  const showDownloadButton = pdf_url || pdfUrl;

  const downloadPDF = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch the file");
      }

      // Extract the filename from the Content-Disposition header
      const disposition = response.headers.get("Content-Disposition");
      let filename = `${certificate_id}.pdf`; // Default filename

      if (disposition && disposition.includes("attachment")) {
        const matches = /filename="([^"]*)"/.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1];
        }
      }

      // Convert the response to a blob
      const blob = await response.blob();

      // Create a URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary link element
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename; // Use the extracted filename
      document.body.appendChild(link);

      // Programmatically click the link to trigger the download
      link.click();

      // Remove the link from the DOM
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  const handleChange = (e) => {
    if (e.target.value.length <= maxChars) {
      setText(e.target.value);
    }
    authContext.isFormDataChangedHandler(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitComment(text);
  };

  const submitComment = (comment) => {
    if (comment) {
      const payload = {
        transaction_id: verificationLogId,
        comments: comment,
      };
      POST(EntityConstants.UPDATE_TRANSACTION_COMMENT, payload)
        .then((res) => {
          if (res.status === 204) {
            setComments(comment);
            setText("");
            defaultNavigate("/verifier/transaction/all");
            clearLegacyStorage();
          } else {
            toast.error(
              "Error occurred while updating comments.\nPlease try again."
            );
          }
        })
        .catch((err) => {
          console.error("Error:", err);
        });
    } else {
      defaultNavigate("/verifier/transaction/all");
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (authContext.isFormDataChanged) {
        authContext.isConfirmationModalOpenHandler(true); // Open modal
        event.preventDefault();
        return "";
      }
    };

    const handlePopState = (event) => {
      if (authContext.isFormDataChanged) {
        authContext.pendingNavigationHandler(event); // Store the event
        authContext.isConfirmationModalOpenHandler(true); // Open modal
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [authContext.isFormDataChanged]);

  const handleConfirmLeave = () => {
    authContext.isConfirmationModalOpenHandler(false);
    authContext.isFormDataChangedHandler(false); // Reset the form change status
    if (typeof authContext.pendingNavigation === "object") {
      navigate(-1);
    } else {
      navigate(authContext.pendingNavigation);
    }
  };

  const handleCancelLeave = () => {
    authContext.isConfirmationModalOpenHandler(false);
    if (authContext.pendingNavigation) {
      window.history.pushState(null, null, window.location.pathname); // Cancel navigation
    }
  };

  useEffect(() => {
    window.history.pushState(null, null, window.location.pathname);
  }, []);

  const paymentStatus = async () => {
    try {
      const response = await POST(`${EntityConstants.LEGACY_PAYMENT_STATUS}`, {
        order_id: lpi_orderId || transaction_order_id,
        payment_id: lpi_paymentId || payment_id,
        payment_signature: lpi_paymentSignature || payment_signature,
        verification_log_id: certificate_id,
      });
      if (response.ok) {
        const data = await response.json();
        setVerificationStatus(data.data.status);
        setVerificationRecipientName(data.data.recipient_name);
        setIssuerName(data.data.issuer_name);
        setVerificationUploadedDocument(data.data.uploaded_document);
        setVerificationLogId(data.data.verification_log_id);
        setComments(data.data.comments || "");
        setText(data.data.comments || "");
        setPdfUrl(data.data.pdf_url);

        localStorage.setItem("org_legacy_code", data.data.org_code);
        localStorage.setItem("recipient_legacy_name", data.data.recipient_name);
        localStorage.setItem(
          "recipient_legacy_phone_number",
          data.data.recipient_phone
        );
        localStorage.setItem(
          "recipient_legacy_email",
          data.data.recipient_email
        );
        localStorage.setItem("programme_legacy_code", data.data.programme_code);
        localStorage.setItem("comments_legacy", data.data.comments);
        localStorage.setItem("document_legacy", data.data.uploaded_document);
      }
    } catch (error) {
      console.error("Error in fetching payment status:", error);
      toast.error("Error in fetching payment status !");
    }
  };

  useEffect(() => {
    // Save variables to localStorage when component mounts
    localStorage.setItem("org_legacy_code", organisation_code);
    localStorage.setItem("recipient_legacy_name", recipient_name);
    localStorage.setItem(
      "recipient_legacy_phone_number",
      recipient_legacy_phone_number
    );
    localStorage.setItem("recipient_legacy_email", recipient_email);
    localStorage.setItem("programme_legacy_code", programme_code);
    localStorage.setItem("comments_legacy", transaction_comments);
    localStorage.setItem("document_legacy", transaction_uploaded_document);

    paymentStatus();
  }, [certificate_id]);

  useEffect(() => {
    // Clear localStorage when certificate_id changes or when navigating away
    return () => {
      clearLegacyStorage();
    };
  }, [certificate_id]);

  return (
    <div className="flex flex-col lg:flex-row w-full pt-20 min-h-screen max-h-screen">
      <ConfirmationModal
        isOpen={authContext.isConfirmationModalOpen}
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
      />
      <section className="flex-1 lg:flex-[7] bg-inherit flex justify-center items-center p-4 lg:p-0">
        <div className="flex justify-center items-center w-full h-full">
          <DocumentPreview documentUrl={verificationUploadedDocument} />
        </div>
      </section>
      <section
        id="details_container"
        className="flex-1 lg:flex-[3] md:min-h-fit bg-cfSecondary p-4 flex flex-col items-center lg:items-start justify-end"
      >
        <div className="text-center lg:text-left flex flex-col items-center self-center">
          <h2 className="text-lg lg:text-xl font-semibold">{certificate_id}</h2>
          <p className="text-sm lg:text-base">Reference ID on CredIssuer</p>
          <span
            className={`flex items-center justify-center mt-2 ${
              verificationStatus === "Valid"
                ? "text-cfPrimary border-2 border-cfPrimary"
                : verificationStatus == "Revoked"
                ? "text-cfSecondary border-red-500 bg-red-500"
                : verificationStatus == "Rejected"
                ? "text-cfSecondary border-red-500 bg-red-500"
                : "text-gray-700 border-2 border-gray-500"
            } w-full py-2 px-4 rounded-full`}
          >
            {verificationStatus && (
              <img
                src={
                  verificationStatus === "Valid"
                    ? Verified
                    : verificationStatus === "Revoked" ||
                      verificationStatus === "Rejected" ||
                      verificationStatus === "Expired"
                    ? Revoked
                    : null
                }
                alt=""
                className="mr-2 w-6 h-6"
              />
            )}
            <p className="text-sm lg:text-base">
              {verificationStatus === "Valid"
                ? "Credential Verified"
                : verificationStatus == "Revoked"
                ? "Verified, but Revoked"
                : verificationStatus == "Rejected"
                ? "Verified, but Rejected"
                : verificationStatus == "Expired"
                ? "Credential Expired"
                : "Loading..."}
            </p>
          </span>
          <div>
            <span className="flex items-center mt-3">
              <p className="text-sm lg:text-base">Issued By:&nbsp;</p>
              <p className="text-sm lg:text-base font-semibold">{issuerName}</p>
            </span>
          </div>
        </div>
        <form
          onSubmit={handleSubmit}
          id="view-legacy-credential-comment-form"
          className="mt-4 w-11/12 min-w-[5rem] max-w-sm self-center"
        >
          <div className="mb-2">
            <textarea
              id="view-legacy-credential-comment-input"
              value={text}
              onChange={handleChange}
              className="w-full h-32 p-2 border border-gray-300 rounded-md resize-none overflow-y-auto"
              placeholder="Enter your comment..."
            />
          </div>
          <div className="text-right text-sm mb-2 text">
            <span>{maxChars - text.length} characters left</span>
          </div>
          <div className="flex mb-2">
            <button
              id="view-legacy-credential-submit-button"
              type="submit"
              className="text-lg w-full font-semibold text-cfSecondary focus:outline-none bg-cfPrimaryButton hover:bg-cfPrimaryButtonHover py-2.5 px-4 rounded-xl"
            >
              Submit
            </button>
          </div>
          {showDownloadButton && (
            <div className="flex mb-28">
              <button
                id="view-legacy-credential-download-report-button"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadPDF(pdf_url || pdfUrl);
                }}
                type="button"
                className="text-lg w-full font-semibold text-cfSecondary focus:outline-none bg-cfPrimaryButton hover:bg-cfPrimaryButtonHover py-2.5 px-4 rounded-xl"
              >
                <div className="flex gap-2 justify-center">
                  <img src={DownloadIcon} alt="" />
                  <div>Download Verification Report</div>
                </div>
              </button>
            </div>
          )}
        </form>
      </section>
    </div>
  );
};

export default ViewLegacyCredential;
